import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import TickerTape from '../components/TickerTape';
import Footer from '../components/Footer';
import { CompanyIcon, fmtPrice } from '../components/CompanyCard';
import { useApp } from '../context/AppContext';
import VaultModal from '../components/modals/VaultModal';
import { Wallet, ShieldCheck, ShieldAlert, Briefcase, TrendingUp, TrendingDown, Plus, Lock } from 'lucide-react';

export default function PortfolioPage() {
  const { holdings, vaultBalance, kycStatus, companies } = useApp();
  const [vaultOpen, setVaultOpen] = useState(false);

  const enriched = holdings.map((h) => {
    const live = companies.find((c) => c.id === h.id);
    const price = live ? live.lastPrice : h.avgPrice;
    const mv = h.shares * price;
    const cost = h.shares * h.avgPrice;
    const pl = mv - cost;
    const plPct = (pl / cost) * 100;
    return { ...h, price, mv, pl, plPct, icon: live?.icon, logo: live?.logo, locked: live?.locked, category: live?.category };
  });

  const totalMv = enriched.reduce((s, h) => s + h.mv, 0);
  const totalPl = enriched.reduce((s, h) => s + h.pl, 0);

  const summary = [
    ['SETTLEMENT VAULT', `$${vaultBalance.toLocaleString()}`, 'text-[var(--green)]'],
    ['POSITIONS VALUE', fmtPrice(totalMv), 'text-white'],
    ['UNREALIZED P/L', `${totalPl >= 0 ? '+' : ''}${fmtPrice(totalPl)}`, totalPl >= 0 ? 'text-[var(--green)]' : 'text-[var(--red)]'],
    ['OPEN POSITIONS', String(enriched.length), 'text-[var(--orange)]'],
  ];

  return (
    <div className="min-h-screen">
      <TickerTape />
      <Navbar />

      <section className="max-w-[1320px] mx-auto px-5 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-[34px] tracking-tight text-white">Portfolio & Escrow</h1>
            <div className={`inline-flex items-center gap-1.5 mt-2 text-[12px] font-mono px-2.5 py-1 rounded-full border ${kycStatus === 'verified' ? 'border-[#1e5b45] text-[var(--green)] bg-[#0e2a20]' : 'border-[#4a2f14] text-[var(--orange)] bg-[#2a1a0e]'}`}>
              {kycStatus === 'verified' ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
              {kycStatus === 'verified' ? 'ACCREDITED • SEC 506(c) VERIFIED' : 'ACCREDITATION PENDING'}
            </div>
          </div>
          <button onClick={() => setVaultOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--orange)] text-white text-[13px] font-semibold hover:bg-[var(--orange-bright)] transition-colors">
            <Plus size={15} /> Fund Vault
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {summary.map(([k, v, c]) => (
            <div key={k} className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-5">
              <div className="text-[9.5px] font-mono tracking-[0.12em] text-[var(--muted-2)]">{k}</div>
              <div className={`font-mono font-semibold text-[22px] mt-2 ${c}`}>{v}</div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase size={16} className="text-[var(--orange)]" />
            <h2 className="font-display font-semibold text-[17px] text-white">Holdings</h2>
          </div>

          {enriched.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--border)] bg-[var(--panel)] py-20 text-center">
              <Wallet size={30} className="mx-auto text-[var(--muted-2)]" />
              <p className="text-[14px] text-[var(--muted)] mt-4">No positions yet.</p>
              <p className="text-[12.5px] text-[var(--muted-2)] mt-1">Fund your vault and buy shares from the Unlisted Markets.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border)] overflow-hidden bg-[var(--panel)] overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-mono tracking-[0.12em] text-[var(--muted-2)] border-b border-[var(--border)]">
                    <th className="py-3 px-5 font-normal">COMPANY</th>
                    <th className="py-3 px-3 font-normal text-right">SHARES</th>
                    <th className="py-3 px-3 font-normal text-right">AVG COST</th>
                    <th className="py-3 px-3 font-normal text-right">LAST</th>
                    <th className="py-3 px-3 font-normal text-right">MKT VALUE</th>
                    <th className="py-3 px-5 font-normal text-right">P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {enriched.map((h) => {
                    const up = h.pl >= 0;
                    return (
                      <tr key={h.id} className="border-b border-[var(--border-soft)] hover:bg-[var(--panel-2)] transition-colors">
                        <td className="py-3.5 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-md bg-[#1c1710] border border-[#3a2a18] flex items-center justify-center overflow-hidden">
                              <CompanyIcon name={h.icon} logo={h.logo} size={15} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] text-white font-medium">{h.name}</span>
                                {h.locked && (
                                  <span className="inline-flex items-center gap-1 text-[8.5px] font-mono px-1.5 py-0.5 rounded bg-[#0e2a20] text-[var(--green)] border border-[#1e5b45]"><Lock size={9} /> LOCKED</span>
                                )}
                              </div>
                              <div className="text-[10px] font-mono text-[var(--orange)]">{h.ticker}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-3 text-right font-mono text-[13px] text-[var(--text)]">{h.shares.toLocaleString()}</td>
                        <td className="py-3.5 px-3 text-right font-mono text-[13px] text-[var(--muted)]">{fmtPrice(h.avgPrice)}</td>
                        <td className="py-3.5 px-3 text-right font-mono text-[13px] text-[var(--orange)]">{fmtPrice(h.price)}</td>
                        <td className="py-3.5 px-3 text-right font-mono text-[13px] text-white">{fmtPrice(h.mv)}</td>
                        <td className="py-3.5 px-5 text-right">
                          {h.locked ? (
                            <span className="text-[10px] font-mono text-[var(--muted-2)] tracking-wider">NON-TRANSFERABLE</span>
                          ) : (
                            <div className={`inline-flex items-center gap-1 font-mono text-[13px] ${up ? 'text-[var(--green)]' : 'text-[var(--red)]'}`}>
                              {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                              {up ? '+' : ''}{fmtPrice(h.pl)} ({h.plPct.toFixed(2)}%)
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <VaultModal open={vaultOpen} onClose={() => setVaultOpen(false)} />
    </div>
  );
}
